import { closestCorners, PointerSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import _ from "underscore";
import { callautopopulate } from "../../../services/callautopop";
import { ToastContext } from "../../../context/ToastContext";
import { dateConvert } from "../../../utils/Helper";

export const useAglieKanban = () => {
    const { showSuccess, showError, showInfo } = useContext(ToastContext);
    const [tasks, setTasks] = useState([]);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 10 } })
    );
    const [activeTask, setActiveTask] = useState(null);
    const [loader, setLoader] = useState(false);
    const [option, setOption] = useState({});
    const [showPopup, setShowPopup] = useState({ show: false, data: {} });
    const [columns, setColumns] = useState([]);
    const [expanded, setExpanded] = useState(true);
    const [selectedValue, setSelectedValue] = useState({});
    const [previousTask, setPreviousTask] = useState([]);
    const [allowedColumns, setAllowedColumns] = useState([]);
    const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
    const [selectedDestination,setSelectedDestination] = useState(null)


    useEffect(() => {
        setAllowedColumns(Object.keys(tasks));
    }, [tasks]);


    useEffect(() => {
        callautopopulate("issue_complexity", "issue_logitivity").then(res => {
            setOption({
                complexity: _.sortBy(res?.issue_complexity, 'order_number'),
                logitivity: _.sortBy(res?.issue_logitivity, 'order_number')
            });
        });
        getKanbanDetails();
    }, []);

    const customCollisionDetection = useCallback((args) => {
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) return pointerCollisions;
        return closestCorners(args);
    }, []);

    // const handleDragStart = useCallback((event) => {
    //     const allTasks = Object.values(tasks).flat();
    //     const task = allTasks.find(t => t.id === event.active.id);
    //     setActiveTask(task);
    // }, [tasks]);
    const handleDragStart = useCallback((event) => {
        const allTasks = Object.values(tasks).flat();
        const task = allTasks.find(t => t.id === event.active.id);

        setActiveTask(task);

        const keys = Object.keys(tasks);
        const sourceIndex = keys.findIndex(key => tasks[key].some(t => t.id === event.active.id));
        const enabledColumns = [
            keys[sourceIndex - 1],
            keys[sourceIndex],
            keys[sourceIndex + 1]
        ].filter(Boolean);

        setAllowedColumns(enabledColumns);
    }, [tasks]);


    const handleDragOver = useCallback((event) => {
        const { over } = event;
        if (!over) return;
        if (!allowedColumns.includes(over.id)) {
        }
    }, [allowedColumns]);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over) return;
        setActiveTask(null);
        setAllowedColumns(Object.keys(tasks));

        let sourceColKey, destColKey;
        for (const key of Object.keys(tasks)) {
            if (tasks[key].some(t => t.id === active.id)) sourceColKey = key;
            if (key === over.id) destColKey = over.id;
        }
        if (!destColKey) {
            for (const key of Object.keys(tasks)) {
                if (tasks[key].some(t => t.id === over.id)) destColKey = key;
            }
        }

        if (!sourceColKey || !destColKey) return;

        if (!allowedColumns.includes(destColKey)) {
            showInfo("info", "Drop cancelled - destination column is disabled");
            return;
        }

        if (sourceColKey === destColKey) {
            return;
        }
        setPreviousTask({ ...tasks });
        setTasks(prevTasks => {
            const newTasks = { ...prevTasks };
            newTasks[sourceColKey] = newTasks[sourceColKey].filter(t => t.id !== active.id);
            const insertIdx = (destColKey === over.id)
                ? newTasks[destColKey].length
                : newTasks[destColKey].findIndex(t => t.id === over.id);
            const movedTask = prevTasks[sourceColKey].find(t => t.id === active.id);
            newTasks[destColKey] = [
                ...newTasks[destColKey].slice(0, insertIdx),
                movedTask,
                ...newTasks[destColKey].slice(insertIdx)
            ];
            if (movedTask) {
                UpdateKanbanBoardBucketStatus(movedTask, destColKey);
            }
            return newTasks;
        });
    }, [tasks, allowedColumns]);


    const getKanbanDetails = async () => {
        try {
            setLoader(true);
            const payload = {
                project_ids: "",
                bucket_ids: "",
                sprint_ids: "",
                issue_type_ids: "",
                epic_ids: ""
            };
            const res = await services.getInvokeRemote("POST", Urls("GETKANBANBOARDVIEWDETAILS"), payload);
            if (!res?.body?.error_status) {
                const tasksArray = res.body.data?.bucket_details || [];
                const defaultTasks = Object.fromEntries(res?.body?.data?.bucket_status?.map(col => [col.status_key_name, []]));
                const groupedTasks = tasksArray.reduce((acc, task) => {
                    const statusKey = task.bucket_status;
                    if (statusKey && acc[statusKey]) {
                        const taskWithId = {
                            ...task,
                            id: task.issue_id || task.id,
                            bid_list: _.sortBy(
                                _.filter(res?.body?.data?.bid_details, { issue_id: task.issue_id }),
                                "bid_seconds"
                            ),
                            winner: _.find(res?.body?.data?.bid_details, { user_code: task.winner_user_code })
                        };
                        acc[statusKey].push(taskWithId);
                    }
                    return acc;
                }, { ...defaultTasks });
                for (const key of Object.keys(groupedTasks)) {
                    groupedTasks[key] = _.uniq(groupedTasks[key], t => t.id);
                }
                setColumns(res?.body?.data?.bucket_status)
                setTasks(groupedTasks);
            }
            setLoader(false);

        } catch (error) {
            setLoader(false);
            console.error("error:::::", error);
        }
    };

    const handleChange = useCallback(async (value, item, name) => {
        try {
            const payload = {
                bucket_id: item?.bucket_id,
                issue_id: item?.issue_id,
                product_code: item?.product_code,
                complexity_id: name === "complexity_id" ? parseInt(value) : parseInt(item?.complexity_id) || 0,
                longitivity_id: name === "longitivity_id" ? parseInt(value) : parseInt(item?.longitivity_id) || 0,
                version_id: 0,
                estimate_seconds: 0
            };
            const res = await services.getInvokeRemote("POST", Urls("UPSERTBUCKETPLANNINGSAVE"), payload);
            if (!res?.body?.error_status) {
                showSuccess('Success', res.body.message);
                setTasks(prevTasks => {
                    const columnKey = Object.keys(prevTasks).find(key => prevTasks[key].some(t => t.id === item.id));
                    if (!columnKey) return prevTasks;
                    const updatedColumnTasks = prevTasks[columnKey].map(task => {
                        if (task.id === item.id) {
                            setShowPopup((prev) => ({ ...prev, data: { ...task, [name]: value } }))
                            return { ...task, [name]: value };
                        }
                        return task;
                    });
                    return { ...prevTasks, [columnKey]: updatedColumnTasks };
                });


            } else {
                showError("error", res?.body?.errorMessage);
            }
        } catch (error) {
            console.error("Error", error);
        }
    }, []);

    const details = {
        Assignee: (
            <>{showPopup?.data?.declare_user_name ? showPopup.data.declare_user_name : "Unassigned"}</>
        ),
        Reporter: <>{showPopup?.data?.bucket_creator_display_name || "-"}</>,
        "Issue Created": "",
        "Actual Start": <>{dateConvert(showPopup?.data?.declared_datetime, "date-time-month") || "-"}</>,
        Priority: <>{showPopup?.data?.priority_name || "-"}</>,
        ...(showPopup?.data?.bucket_status === "Planning" && {
            Complexity: (
                <select
                    className="input-dd w100"
                    name="complexity_id"
                    value={showPopup?.data?.complexity_id || ""}
                    onChange={(e) => handleChange(e.target?.value, showPopup?.data, "complexity_id")}
                    onPointerDown={e => e.stopPropagation()}
                >
                    <option value="">Complexity</option>
                    {option?.complexity?.map(opt => (
                        <option key={opt.issue_complexity_id} value={opt.issue_complexity_id}>
                            {opt.issue_complexity_name}
                        </option>
                    ))}
                </select>
            ),
            Logitivity: (
                <select
                    className="input-dd w100"
                    name="longitivity_id"
                    value={showPopup?.data?.longitivity_id || ""}
                    onChange={(e) => handleChange(e.target?.value, showPopup?.data, "longitivity_id")}
                    onPointerDown={e => e.stopPropagation()}
                >
                    <option value="">Logitivity</option>
                    {option?.logitivity?.map((opt, index) => (
                        <option key={index} value={opt.issue_logitivity_id}>
                            {opt.issue_logitivity_name}
                        </option>
                    ))}
                </select>
            )
        }),
        Parent: (
            <div className="agile-card">
                <p className="parent">
                    {showPopup?.data?.feature_issue_summary?.length
                        ? showPopup.data.feature_issue_summary.length > 30
                            ? `${showPopup.data.feature_issue_summary.slice(0, 30)}...`
                            : showPopup.data.feature_issue_summary
                        : "-"}
                </p>
            </div>
        ),
    };


    const UpdateKanbanBoardBucketStatus = async (task, key) => {
        try {

            const payload = {
                "bucket_status_id": _.find(columns, { status_key_name: key })?.bucket_status_id,
                "bucket_issue_id": task?.bucket_issue_id
            }
            const res = await services.getInvokeRemote("POST", Urls("UPDATEKANBANBOARDBUCKETSTATUS"), payload);
            if (res?.body?.StatusCode === 400 || res?.body?.StatusCode === 204 || res?.body?.StatusCode === 202 || res?.body?.StatusCode === 401 || res?.body?.StatusCode === 500) {
                showError("error", res?.body?.errorMessage);
                setTasks({ ...previousTask })
            } else if (!res?.body?.error_status) {
                showSuccess("success", res.body.message);
            } else {
                setTasks({ ...previousTask })
                showError("error", res.body.message);
            }
        } catch (error) {
            setLoader(false);
            console.error("error:::::", error);
        }
    }


    const moveSelectedToColumn = (destColKey) => {
        if (!destColKey) return;
        setTasks(prevTasks => {
            const newTasks = { ...prevTasks };
            const srcColumns = Object.keys(newTasks).filter(col =>
                newTasks[col].some(task => selectedTaskIds.has(task.id))
            );
            srcColumns.forEach(col => {
                newTasks[col] = newTasks[col].filter(task => !selectedTaskIds.has(task.id));
            });

            const movedTasks = [];
            srcColumns.forEach(col => {
                movedTasks.push(...prevTasks[col].filter(task => selectedTaskIds.has(task.id)));
            });
            newTasks[destColKey] = [...movedTasks,...newTasks[destColKey]];

            return newTasks;
        });
        setSelectedDestination(null)
        setSelectedTaskIds(new Set());
    };

    return useMemo(() => ({
        tasks, sensors, activeTask, handleDragStart, handleDragEnd, columns, loader, option, customCollisionDetection, handleChange, showPopup, setShowPopup, details, expanded, setExpanded, selectedValue, setSelectedValue, allowedColumns, handleDragOver,moveSelectedToColumn,selectedTaskIds,selectedDestination,setSelectedDestination,setSelectedTaskIds
    }), [tasks, sensors, activeTask, handleDragStart, handleDragEnd, columns, loader, option, customCollisionDetection, handleChange, showPopup, setShowPopup, details, expanded, setExpanded, selectedValue, setSelectedValue, allowedColumns, handleDragOver,moveSelectedToColumn,selectedTaskIds,selectedDestination,setSelectedDestination,setSelectedTaskIds]);
};
